import { GetSignupsByShiftIdCommandOutput, Signup } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { useAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';
import { AmazonSignup, SignupStatus } from '../../utils/signupUtils';

const DEFAULT_PAGE_SIZE = 1000;

function useTShirtLabelMapper() {
  const { t } = useTranslation('translation');
  const tShirtSizes = t('eventDetails.shiftDisplay.complexSignup.tshirtSizes', {
    returnObjects: true,
  }) as { label: string; value: string }[];

  return (id?: string) => {
    if (!id) return '';
    return tShirtSizes.find((s) => s.value === id)?.label || id;
  };
}

function toAmazonSignup(signup: Signup, getTShirtLabel: (id?: string) => string): AmazonSignup {
  const { signupUserInfo } = signup;
  return {
    signupId: signup.signupId || '',
    shiftId: signup.shiftId || '',
    name: `${signupUserInfo?.firstName || ''} ${signupUserInfo?.lastName || ''}`.trim(),
    alias: signupUserInfo?.alias || '',
    email: signupUserInfo?.primaryEmail || '',
    building: signupUserInfo?.building || '',
    managerLogin: signupUserInfo?.managerLogin || '',
    numberOfGuests: signup.guestCount ?? 0,
    tShirtSize: getTShirtLabel(signup.tShirtSizeId?.toString()),
    signupStatus: SignupStatus.SIGNUP,
  };
}

export function useAllSignupsByShiftId(shiftId: string, enabled = true) {
  const getTShirtLabel = useTShirtLabelMapper();

  const result = useAllPagesRedVelvetQuery({
    enabled: enabled && shiftId !== '',
    redVelvetQueryKey: [
      'getSignupsByShiftId',
      { shiftId, includeSignupUserInfo: true, pageSize: DEFAULT_PAGE_SIZE },
    ],
    converter: (output: GetSignupsByShiftIdCommandOutput) => ({
      items: output.signups || [],
      next: output.page,
    }),
  });

  return {
    signups: (result.data || []).map((signup) => toAmazonSignup(signup, getTShirtLabel)),
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
  };
}
